// Reserve the placard row per ROW of cards, not per card.
//
// Every card carries a .pcard__label-row so a badge ("Sold Out", "6 colors")
// has somewhere to sit and so neighbouring cards line their titles up. Holding
// it open on every card spends 36px above every title — the 20px row plus the
// 1rem .pcard__content gap — including on rows where nothing wears a badge.
//
// A row of cards is not an element, so CSS cannot ask "does anything in my row
// have a badge?": there is no selector for it, and subgrid would mean threading
// tracks down three levels of a card that nine sections share. So walk the
// container instead. Rows come from the computed grid-template-columns, which
// makes a row a run of that many visible children — no geometry is measured, so
// this never waits on images.
//
// The flag marks rows with NO badge anywhere: their empty placard row collapses
// and the text pulls up against the image. A row carrying even one badge keeps
// the space on all of its cards, so the titles stay level across the row.
const BADGE = '.pcard__label';
const BADGELESS = 'data-row-badgeless';

customElements.define(
	'card-rows',
	class extends HTMLElement {
		connectedCallback() {
			// Usually the container is the only child — <card-rows> is
			// display:contents, so it adds no box and the container keeps its place
			// in the layout. A carousel track cannot be wrapped that way: its own
			// script reads track.parentElement to find the scroller. There the
			// element goes round the carousel and data-cards points at the track.
			this.cards = this.querySelector('[data-cards]') || this.firstElementChild;
			if (!this.cards) return;
			this.tidy = this.tidy.bind(this);
			this.schedule = this.schedule.bind(this);
			// childList catches Load more appending cards, a carousel cloning its
			// slides, and the account pages dropping an unfavourited one; hidden
			// catches in-collection search taking a card out of the row (see
			// applyCollectionSearch in collection-sheets.js). Filtering to `hidden`
			// also keeps our own flag writes from waking the observer.
			this.watch = new MutationObserver(this.schedule);
			this.watch.observe(this.cards, {
				childList: true,
				subtree: true,
				attributeFilter: ['hidden'],
			});
			window.addEventListener('resize', this.schedule);
			this.tidy();
		}

		disconnectedCallback() {
			this.watch?.disconnect();
			window.removeEventListener('resize', this.schedule);
			cancelAnimationFrame(this.frame);
		}

		schedule() {
			cancelAnimationFrame(this.frame);
			this.frame = requestAnimationFrame(this.tidy);
		}

		// How many cards make a row. `columns` states it where the container is not
		// a CSS grid: "all" for a carousel, whose whole track reads as one row, or
		// a number for a plain list, which stacks one card per row.
		columns() {
			const declared = this.getAttribute('columns');
			if (declared === 'all') return Infinity;
			if (declared) return parseInt(declared, 10);
			const template = getComputedStyle(this.cards).gridTemplateColumns;
			return template && template !== 'none' ? template.split(' ').length : 0;
		}

		tidy() {
			// Leave an unrendered container alone. Its cards are not on screen to
			// judge, and getComputedStyle answers there with the template as
			// authored — "repeat(4, 1fr)" — which would count as two columns. The
			// favorites page ships its grid inside a [hidden] wrapper, so this is a
			// real state rather than a theoretical one.
			if (!this.cards.getClientRects().length) return;
			const columns = this.columns();
			if (!(columns > 0)) return;
			const cards = Array.from(this.cards.children).filter((card) => !card.hidden);
			for (let start = 0; start < cards.length; start += columns) {
				const row = cards.slice(start, start + columns);
				const badged = row.some((card) => card.querySelector(BADGE));
				for (const card of row) card.toggleAttribute(BADGELESS, !badged);
			}
		}
	}
);
